import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';

interface SmsConfig {
  id: string;
  provider: string;
  apiKey: string;
  senderId: string;
  username?: string;
  entityId?: string;
  isActive: boolean;
  schoolId?: string | null;
}

const SmsSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role?.code === 'SUPER_ADMIN';
  const [config, setConfig] = useState<SmsConfig | null>(null);
  const [isInherited, setIsInherited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    provider: 'MSG91',
    apiKey: '',
    senderId: '',
    username: '',
    entityId: '',
    isActive: true,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Test Dialog State
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTarget, setTestTarget] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/system-settings/communication/sms-config');
      if (data && data.length > 0) {
        const active = data.find((c: SmsConfig) => c.isActive) || data[0];
        const inherited = !isSuperAdmin && !active.schoolId;
        setIsInherited(inherited);
        setConfig(inherited ? null : active);
        setFormData({
          provider: active.provider,
          apiKey: active.apiKey, // Backend should mask this
          senderId: active.senderId,
          username: active.username || '',
          entityId: active.entityId || '',
          isActive: active.isActive,
        });
      }
    } catch (error) {
      console.error('Failed to fetch SMS config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleTestOpen = () => {
    setTestTarget('');
    setTestDialogOpen(true);
  };

  const handleTestClose = () => {
    setTestDialogOpen(false);
  };

  const handleTestSubmit = async () => {
    if (!config || !testTarget) return;
    
    setTesting(true);
    try {
      const { data } = await axios.post(`/system-settings/communication/sms-config/${config.id}/test`, {
        target: testTarget
      });
      
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        handleTestClose();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to send test SMS' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      if (config && !isInherited) {
        await axios.patch(`/system-settings/communication/sms-config/${config.id}`, formData);
        setMessage({ type: 'success', text: 'SMS Configuration updated successfully' });
      } else {
        await axios.post('/system-settings/communication/sms-config', formData);
        setMessage({ type: 'success', text: isInherited ? 'School SMS override created successfully' : 'SMS Configuration created successfully' });
        fetchConfig();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save configuration' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <>
      {/* PAGE HEADER */}
      <div className="page-header">
        <h1>SMS Configuration</h1>
        <p>Configure SMS provider, sender ID and credentials</p>
      </div>

      {/* PAGE BODY */}
      <Paper sx={{ p: 3 }}>
        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {isInherited && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You are viewing SuperAdmin default settings. Saving this form will create your school-specific override.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Provider</InputLabel>
              <Select
                name="provider"
                value={formData.provider}
                label="Provider"
                onChange={handleChange as any}
              >
                <MenuItem value="MSG91">MSG91</MenuItem>
                <MenuItem value="TWILIO">Twilio</MenuItem>
                <MenuItem value="TEXTLOCAL">TextLocal</MenuItem>
                <MenuItem value="FAST2SMS">Fast2SMS</MenuItem>
                <MenuItem value="KALEYRA">Kaleyra</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Sender ID (Header)"
              name="senderId"
              value={formData.senderId}
              onChange={handleChange}
              required
              helperText="6-character Sender ID approved by DLT"
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              label="API Key / Auth Token"
              name="apiKey"
              value={formData.apiKey}
              onChange={handleChange}
              required
              type="password"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Entity ID (DLT)"
              name="entityId"
              value={formData.entityId}
              onChange={handleChange}
              helperText="Principal Entity ID registered on DLT platform"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Username (Optional)"
              name="username"
              value={formData.username}
              onChange={handleChange}
              helperText="Required for some providers like TextLocal"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label={formData.isActive ? "Active" : "Inactive"}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            
            {config && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SendIcon />}
                onClick={handleTestOpen}
                disabled={!config.isActive}
              >
                Test Configuration
              </Button>
            )}
          </Grid>
        </Grid>
      </form>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={handleTestClose}>
        <DialogTitle>Send Test SMS</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a phone number to send a test SMS using the current configuration.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={testTarget}
            onChange={(e) => setTestTarget(e.target.value)}
            placeholder="+919876543210"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestClose}>Cancel</Button>
          <Button 
            onClick={handleTestSubmit} 
            variant="contained" 
            disabled={!testTarget || testing}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </>
  );
};
 
export default SmsSettings;
